import React, {useMemo, useState} from "react";
import {Box, Button, Menu, MenuItem, Typography} from "@mui/material";
import {RiArrowDownSLine, RiArrowUpSLine} from "@remixicon/react";
import {hiddenNetworks, NetworkName, networks} from "../../constants";
import {useGlobalState} from "../../global-config/GlobalConfig";
import useHoverDropdown from "../../components/hooks/useHoverDropdown";

export default function NetworkSelect() {
  const [state, {selectNetwork}] = useGlobalState();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dropdown = useHoverDropdown();
  const open = dropdown.open;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    dropdown.show();
  };

  const handleClose = () => {
    dropdown.close();
  };

  const visibleNetworkNames = useMemo(
    () =>
      Object.keys(networks).filter(
        (n) => !hiddenNetworks.includes(n as NetworkName),
      ),
    [],
  );

  return (
    <Box>
      <Button
        id="network-select-button"
        aria-controls={open ? "network-select-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        onMouseEnter={(event) => {
          setAnchorEl(event.currentTarget);
          dropdown.triggerProps.onMouseEnter();
        }}
        onMouseLeave={dropdown.triggerProps.onMouseLeave}
        variant="outlined"
        sx={(theme) => ({
          borderRadius: "8px",
          height: 40,
          px: 1.5,
          py: 0,
          minWidth: 108,
          color: theme.palette.tokens.text.primary,
          backgroundColor:
            theme.palette.mode === "dark"
              ? open
                ? theme.palette.tokens.line.brand
                : "transparent"
              : theme.palette.tokens.layer.card,
          border: `1px solid ${
            open
              ? theme.palette.tokens.line.input
              : theme.palette.tokens.line.subtle
          }`,
          textTransform: "none",
          fontSize: 16,
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.75,
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.tokens.layer.brandActive
                : theme.palette.tokens.layer.card,
            border: `1px solid ${
              open
                ? theme.palette.tokens.line.input
                : theme.palette.tokens.line.strong
            }`,
          },
          "&:active, &[aria-expanded=true]": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.tokens.line.brand
                : theme.palette.tokens.layer.card,
            border: `1px solid ${theme.palette.tokens.line.input}`,
          },
        })}
      >
        <Typography
          component="span"
          sx={{
            textTransform: "capitalize",
            fontSize: 16,
            fontWeight: 500,
            color: "inherit",
            lineHeight: 1.4,
          }}
        >
          {state.network_name}
        </Typography>
        {open ? (
          <RiArrowUpSLine size={18} style={{opacity: 0.7}} />
        ) : (
          <RiArrowDownSLine size={18} style={{opacity: 0.7}} />
        )}
      </Button>
      <Menu
        id="network-select-menu"
        disableScrollLock
        autoFocus={!dropdown.openedByHover}
        disableAutoFocus={dropdown.openedByHover}
        disableEnforceFocus={dropdown.openedByHover}
        disableRestoreFocus={dropdown.openedByHover}
        sx={{pointerEvents: dropdown.openedByHover ? "none" : "auto"}}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "network-select-button",
          sx: {p: 0.5},
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            ...dropdown.paperProps,
            sx: (theme) => ({
              pointerEvents: "auto",
              width: anchorEl ? anchorEl.offsetWidth : "auto",
              minWidth: anchorEl ? anchorEl.offsetWidth : "auto",
              mt: 0,
              p: 0.5,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.tokens.line.subtle}`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.4)"
                  : "0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)",
              backgroundColor: theme.palette.tokens.layer.modal,
            }),
          },
        }}
      >
        {visibleNetworkNames.map((networkName) => {
          const isSelected = state.network_name === networkName;
          return (
            <MenuItem
              key={networkName}
              selected={isSelected}
              onClick={() => {
                selectNetwork(networkName as NetworkName);
                handleClose();
              }}
              sx={(theme) => ({
                borderRadius: "6px",
                px: 1.5,
                py: 0.75,
                my: 0,
                fontSize: 14,
                lineHeight: 1.4,
                fontWeight: isSelected ? 500 : 400,
                textTransform: "capitalize",
                color: isSelected
                  ? theme.palette.tokens.function.primary
                  : theme.palette.tokens.text.primary,
                backgroundColor: isSelected
                  ? theme.palette.mode === "dark"
                    ? theme.palette.tokens.line.brand
                    : theme.palette.tokens.layer.brandActive
                  : "transparent",
                "&.Mui-selected, &.Mui-selected.Mui-focusVisible": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.tokens.line.brand
                      : theme.palette.tokens.layer.brandActive,
                  "&:hover": {
                    backgroundColor: theme.palette.tokens.layer.brandActive,
                  },
                },
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.tokens.layer.brandActive
                      : isSelected
                        ? theme.palette.tokens.layer.brandActive
                        : theme.palette.tokens.background.secondary,
                  color: isSelected
                    ? theme.palette.tokens.function.primary
                    : theme.palette.tokens.text.primary,
                },
              })}
            >
              {networkName}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
